/**
 * Centralised API client for the React SPA.
 * - Uses fetch with credentials (session cookie).
 * - Throws ApiError on non-2xx responses.
 * - TanStack Query hooks live alongside each feature module; this file
 *   provides the low-level fetch primitives.
 */

import { withFieldLabel } from '@physioedge/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Prefer field-level Zod messages over the generic "Validation failed." */
  get friendlyMessage(): string {
    if (Array.isArray(this.details) && this.details.length > 0) {
      const parts = this.details
        .map((d) => {
          if (d && typeof d === 'object' && 'message' in d && typeof (d as { message: unknown }).message === 'string') {
            const message = (d as { message: string }).message;
            const path = 'path' in d && typeof (d as { path: unknown }).path === 'string' ? (d as { path: string }).path : '';
            return withFieldLabel(path, message);
          }
          return null;
        })
        .filter((m): m is string => !!m);
      if (parts.length > 0) return parts.join(' ');
    }
    return this.message;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      body = await res.json();
    } catch { /* empty */ }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'unknown',
      body.error?.message ?? `HTTP ${res.status}`,
      body.error?.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'GET' }),

  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: 'POST',
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: 'PATCH',
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: 'PUT',
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'DELETE' }),

  // Clinic API domain helpers (unwrap server response envelopes)
  getClinicStats: () => request<any>('/api/clinic/stats'),
  getClinicBookings: async () => {
    const res = await request<{ bookings: Booking[] }>('/api/clinic/bookings?limit=100');
    return res.bookings ?? [];
  },
  getClinicWithdrawals: async () => {
    const res = await request<{ requests: WithdrawalRequest[]; total: number }>(
      '/api/clinic/withdrawals?limit=100',
    );
    return res.requests ?? [];
  },
  getClinicMe: () =>
    request<{
      clinic: Clinic;
      locations: ClinicLocation[];
      isPrimary?: boolean;
      locationIds?: string[];
    }>('/api/clinic/me'),
  getClinicConsultations: async () => {
    const res = await request<{
      consultations: Array<{
        id: string;
        studentId?: string;
        studentName: string;
        doctorId?: string;
        doctorName?: string;
        question: string;
        answer: string | null;
        status: string;
        responseDeadline: string;
        answeredAt: string | null;
        createdAt: string;
      }>;
      doctors?: Array<{
        id: string;
        fullName: string;
        specialty?: string | null;
        photoUrl?: string | null;
        acceptingConsultations?: boolean;
        isActive?: boolean;
      }>;
      doctor: {
        id: string;
        fullName: string;
        specialty?: string | null;
        photoUrl?: string | null;
        acceptingConsultations?: boolean;
        isActive?: boolean;
      } | null;
    }>('/api/clinic/consultations');
    return res;
  },
  getExploreStudents: () =>
    request<{
      students: Array<{
        exploreToken: string;
        displayName: string;
        photoUrl: string | null;
        physioEdgeRating: number | null;
        weeksTaken: number;
        attendancePercent: number | null;
        certificateCount: number;
        evaluationCount: number;
        rating: number | null;
      }>;
    }>('/api/clinic/students/explore'),
  getExploreStudentDetail: (token: string) =>
    request<{
      student: {
        exploreToken: string;
        displayName: string;
        photoUrl: string | null;
        physioEdgeRating: number | null;
        ratingBreakdown: {
          clinicEvaluations: number | null;
          reliability: number | null;
          experience: number | null;
          attendance: number | null;
        };
        weeksTaken: number;
        cancelledWeeks: number;
        attendancePercent: number | null;
        certificateCount: number;
        evaluationCount: number;
        dimensionAverages: {
          professionalism: number;
          ethicalCode: number;
          timeRespect: number;
          communication: number;
        } | null;
        recentEvaluations: Array<{
          sourceLabel: string;
          professionalism: number;
          ethicalCode: number;
          timeRespect: number;
          communication: number;
          average: number;
          comment: string | null;
          weekStartDate: string;
        }>;
      };
    }>(`/api/clinic/students/explore/${encodeURIComponent(token)}`),
  confirmAttendance: (bookingId: string, sessionDate: string) =>
    request(`/api/clinic/bookings/${bookingId}/attendance`, {
      method: 'PATCH',
      body: JSON.stringify({ sessionDate }),
    }),
  completeBooking: (bookingId: string) =>
    request(`/api/clinic/bookings/${bookingId}/complete`, { method: 'PATCH' }),
  evaluateBooking: (
    bookingId: string,
    payload: {
      professionalism: number;
      ethicalCode: number;
      timeRespect: number;
      communication: number;
      comment?: string;
    },
  ) =>
    request(`/api/clinic/bookings/${bookingId}/evaluation`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  issueCertificate: (bookingId: string) =>
    request(`/api/clinic/bookings/${bookingId}/certificate`, { method: 'POST' }),
  answerConsultation: (consultationId: string, answer: string) =>
    request(`/api/clinic/consultations/${consultationId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    }),
  /** Withdraws the full available clinic balance (server does not accept a partial amount). */
  requestWithdrawal: () =>
    request<{ amountEgp: number }>('/api/clinic/withdrawals', { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Typed response shapes (subset used by components)
// ---------------------------------------------------------------------------

export type Me = {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  role: string;
  mustChangePassword?: boolean;
  needsClinicOnboarding?: boolean;
  needsPartnerTerms?: boolean;
  isPrimaryClinicStaff?: boolean;
  studentId?: string;
  approvalStatus?: string;
  approvedAt?: string | null;
  hasPhoto?: boolean;
  photoUrl?: string | null;
  restriction?: {
    id: string;
    blockInternships: boolean;
    blockConsultations: boolean;
    endsAt: string | null;
    reason: string | null;
  } | null;
  doctorId?: string;
  clinicId?: string;
  partnerTermsText?: string | null;
  adminId?: string;
  isSuperAdmin?: boolean;
  /** Admin module:action keys when not super-admin. */
  permissions?: string[];
};

export type PlanFeaturePoint = {
  id: string;
  text: string;
  sortOrder: number;
  /** Check when true, X when false. Shared points are always a check. */
  positive?: boolean;
};

export type Plan = {
  id: string;
  name: string;
  description: string | null;
  priceEgp: number;
  compareAtPriceEgp: number;
  durationWeeks: number;
  internshipWeeksIncluded: number;
  consultationCreditsIncluded: number;
  isActive: boolean;
  showClinics?: boolean;
  sortOrder: number;
  points?: PlanFeaturePoint[];
  /** Empty = all clinics allowed for internships. */
  clinicIds?: string[];
  clinics?: { id: string; name: string }[];
};

export type ClinicPartnerAgreementAcceptance = {
  clinicNameTyped: string;
  representativeName: string;
  representativeTitle: string;
  acknowledgmentDate: string;
  agreementVersion: string;
  submittedAt: string;
  acceptedBy: { fullName: string; email: string } | null;
};

export type Clinic = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  payoutRatePerWeekEgp: number;
  studentPolicyText?: string | null;
  partnerTermsText?: string | null;
  platformTermsAcceptedAt?: string | null;
  partnerAgreementAcceptance?: ClinicPartnerAgreementAcceptance | null;
  isActive: boolean;
  /** When false, internship booking is blocked (clinic can still be listed). */
  bookingEnabled?: boolean;
};

export type ClinicLocation = {
  id: string;
  name: string;
  city: string;
  addressLine: string;
  mapUrl: string | null;
  phone: string | null;
  isActive: boolean;
};

export type Doctor = {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  phone?: string;
  specialty: string | null;
  bio: string | null;
  photoUrl: string | null;
  consultationRateEgp: number;
  responseWindowDays: number | null;
  acceptingConsultations: boolean;
  referralCode: string;
  referralRewardRegisteredEgp?: number | null;
  referralPlanRewardMode?: 'default' | 'fixed' | 'percent';
  referralPlanRewardValue?: number | null;
  isActive: boolean;
  clinicId?: string | null;
};

export type EventItem = {
  id: string;
  title: string;
  slug: string;
  body: string;
  photoUrl: string | null;
  photoKind: 'image' | 'video' | null;
  isPublished: boolean;
  /** Readable by everyone (guests). False = members-only (plan gating later). */
  isPublic: boolean;
  isRead?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type BlogCategory = {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  postCount?: number;
};

export type BlogPost = {
  id: string;
  title: string;
  body: string;
  coverUrl: string | null;
  coverKind: 'image' | 'video' | null;
  thumbnailUrl?: string | null;
  categoryId: string;
  category: { id: string; name: string; slug: string } | null;
  isPublished: boolean;
  /** Readable by everyone (guests). False = members-only. */
  isPublic: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Preview teaser — title/cover only, not readable without a plan. */
  locked?: boolean;
};

export type PlanClock = {
  bookingOpensAt: string;
  durationStartsAt: string | null;
  endsAt: string | null;
  mustBookFirstWeekBy: string | null;
};

export type Subscription = {
  id: string;
  planId: string;
  planName: string;
  status: string;
  pricePaidEgp: number;
  durationWeeks?: number;
  internshipWeeksIncluded: number;
  consultationCreditsIncluded: number;
  internshipCreditsRemaining: number;
  consultationCreditsRemaining: number;
  startDate: string | null;
  expiryDate: string | null;
  firstBookDeadline?: string | null;
  activatedAt?: string | null;
  planClock?: PlanClock;
};

export type Booking = {
  id: string;
  studentId: string;
  studentName?: string;
  studentEmail?: string;
  studentNationalId?: string;
  studentPhotoUrl?: string | null;
  clinicName?: string;
  subscriptionId: string;
  scheduleId: string;
  clinicLocationId: string;
  locationName?: string;
  /** Staff numbers for this booking's branch. Null until 1 day before the first session. */
  staffPhones?: string[] | null;
  mapUrl?: string | null;
  firstSessionDate?: string;
  scheduleTitle?: string;
  clinicId: string;
  weekStartDate: string;
  weekEndDate: string;
  slotStartTime: string;
  slotEndTime: string;
  dates?: string[];
  status: string;
  attended: boolean;
  sessionCount?: number;
  createdAt: string;
  hasEvaluation?: boolean;
  hasCertificate?: boolean;
  hasStudentRating?: boolean;
  canRateClinic?: boolean;
  evaluation?: {
    professionalism: number;
    ethicalCode: number;
    timeRespect: number;
    communication: number;
    comment: string | null;
    average: number;
  } | null;
  phase?: 'cancelled' | 'upcoming' | 'in_progress' | 'past';
  canMarkAttendance?: boolean;
  canMarkComplete?: boolean;
  canEvaluate?: boolean;
  canIssueCertificate?: boolean;
  overallRating?: number | null;
  evaluationCount?: number;
  weekPercent?: number;
  attendanceDays?: Array<{ date: string; attended: boolean; canMark: boolean }>;
};

export type Consultation = {
  id: string;
  studentId: string;
  doctorId: string;
  doctorName: string;
  question: string;
  answer: string | null;
  status: string;
  questionModerationStatus?: string;
  answerModerationStatus?: string | null;
  moderationNote?: string | null;
  responseDeadline: string;
  answeredAt: string | null;
  rateEgp: number;
  createdAt: string;
};

export type AdminConsultation = {
  id: string;
  status: string;
  question: string;
  answer: string | null;
  questionModerationStatus: string;
  answerModerationStatus: string | null;
  moderationNote: string | null;
  needsReview: boolean;
  createdAt: string;
  answeredAt: string | null;
  responseDeadline: string;
  channel: 'mentor' | 'clinic';
  student: { id: string; name: string; email: string };
  mentor: { id: string; name: string };
  clinic: { id: string; name: string } | null;
};

export type Certificate = {
  id: string;
  certId: string;
  bookingId: string;
  studentName: string;
  clinicName: string;
  trainingStartDate: string;
  trainingEndDate: string;
  pdfUrl: string;
  generatedAt: string;
};

export type ReferralActivityItem = {
  id: string;
  displayName: string;
  action: string;
  eventType: 'registered' | 'subscribed';
  rewardEgp: number;
  status: 'available' | 'pending' | 'paid';
  createdAt: string;
};

export type ReferralSummary = {
  code: string;
  /** True when this account already linked someone else's referral code. */
  hasReferrer?: boolean;
  registeredCount: number;
  subscribedCount: number;
  totalEarnedEgp: number;
  paidEgp: number;
  pendingRequestEgp: number;
  availableEgp: number;
  activity: ReferralActivityItem[];
};

export type WithdrawalRequest = {
  id: string;
  source: string;
  amountEgp: number;
  itemCount: number;
  status: string;
  createdAt: string;
  paidAt: string | null;
};

export type WeekOption = {
  weekStartDate: string;
  weekEndDate: string;
  slotStartTime: string;
  slotEndTime: string;
  dates: string[];
  available: boolean;
};

export function formatBookingDays(b: { dates?: string[]; weekStartDate: string; weekEndDate: string }): string {
  if (b.dates && b.dates.length > 0) {
    return b.dates.map(value => {
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(year!, month! - 1, day!);
      return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    }).join(', ');
  }
  return `${b.weekStartDate} → ${b.weekEndDate}`;
}

export function egp(amountEgp: number): string {
  return `${amountEgp.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} EGP`;
}
